import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import Icon from '@/shared/components/Icon';
import axios, {AxiosRequestConfig} from 'axios';
import DashboardLayout from '@/shared/layout/DashboardLayout';
import LabResultsHeader from '../components/LabResultsHeader';
import Button from '@/shared/components/Button';
import ResultsView from '../components/ResultsView';
import BinaryResult from '../components/BinaryResult';
import PatientContextModal from '../components/PatientContextModal';
import {getNameString} from '@/shared/utils/Test';
import {isPatientRole} from '@mld/shared/constants/userRoles';
import {decodeJwtToken} from '@/shared/utils/tokenUtils';
import settings from '@/settings';
import type {Test} from '@/shared/types/api';
import {logError} from '@/shared/utils/logger';
import {DEFAULT_DOWNLOAD_ERROR, readDownloadError, resolvePdfAvailability} from '../utils/pdfAvailability';

type SingleResultContentProps = {
    result: Test;
    realResult: Test | null;
    token?: string;
    backTo: string;
    backLabel: string;
    allPatientData?: Test[] | null;
    patientGender?: 'male' | 'female';
    /** Triggers a silent refetch of the order — used after a clinical-flag change
     *  so the displayed reference ranges update in place. */
    onOrderRefetch?: () => void;
    /** Optional content rendered to the right of the Back button — used by
     *  provider-side wrappers to attach actions like "Edit Patient". */
    headerActions?: React.ReactNode;
    /** Patient's user ID — required for the Patient tab of the combined
     *  Patient + Clinical Context modal.  Threaded through from the route
     *  because Test/order objects don't carry users.id. */
    patientUserId?: number | null;
};

type MsSaveBlobNavigator = Navigator & {msSaveOrOpenBlob?: (blob: Blob) => void};

const SingleResultContent: React.FC<SingleResultContentProps> = ({
    result,
    realResult,
    token,
    backTo,
    backLabel,
    allPatientData,
    patientGender,
    onOrderRefetch,
    headerActions,
    patientUserId = null,
}) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [, setMenuVisible] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const [aboutTestVisible, setAboutTestVisible] = useState(false);
    const pdfAvailability = useMemo(() => resolvePdfAvailability(result, realResult), [result, realResult]);
    const [patientContextOpen, setPatientContextOpen] = useState(false);

    // Initialize filter from URL parameter if present
    const statusParam = searchParams.get('status');
    const [filterBy, setFilterBy] = useState(
        statusParam && ['normal', 'borderline', 'critical'].includes(statusParam) ? statusParam : 'all'
    );

    const [sortBy, setSortBy] = useState('date');
    const [viewMode, setViewMode] = useState<'panel' | 'biomarker'>('panel');
    const menuRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);

    const createdDateString = result?.createdDate
        ? new Date(result.createdDate).toLocaleDateString('en-US', {timeZone: 'UTC'})
        : 'N/A';
    const statusDateString = result?.statusDate
        ? new Date(result.statusDate).toLocaleDateString('en-US', {timeZone: 'UTC'})
        : 'N/A';

    const oidPrefix = result?.oid?.substring(result.oid.length - 10, result.oid.length - 8) || '';
    const productLineCode = result?.productLineCode?.trim().toUpperCase() || '';
    const productLineName = result?.productLineName?.trim().toLowerCase() || '';

    const isBlood =
        (productLineCode && ['BW', 'AL', 'ST', 'CD'].includes(productLineCode)) ||
        productLineName.includes('blood') ||
        ['BW', 'AL', 'ST', 'CD'].includes(oidPrefix);
    const isAllergy = productLineCode === 'AL' || productLineName.includes('allergy') || oidPrefix === 'AL';

    const hasMultipleResults =
        ['BW', 'CF', 'BT', 'AL', 'ST'].includes(productLineCode || oidPrefix) || productLineName.includes('blood');
    const isPanel = ['BW', 'AL'].includes(productLineCode || oidPrefix) || isBlood || isAllergy;
    const hasAboutTest = ['AL', 'AB', 'CV', 'RA', 'ST', 'RP'].includes(productLineCode || oidPrefix);

    // Determine if this test supports panel/biomarker views and filtering
    const supportsPanelView = result?.resultType === 'allergy' || result?.resultType === 'blood' || isPanel;

    const testNameString = result ? getNameString(result) : '';
    const payload = token ? decodeJwtToken(token) : null;
    const roleId = payload?.role_id ?? null;
    const isNonPatientViewer = roleId != null && !isPatientRole(roleId);
    // NOTE: keeping this around — if we later decide that admins
    // impersonating a patient should also see Clinical Context (and other
    // provider-only affordances) from the patient-facing /result/:id
    // route, swap the line above for the OR form below.  `impersonate_id`
    // is the canonical "actual viewer is an admin" breadcrumb
    // (tokenUtils.ts:55-60 uses the same check).
    // const isImpersonating = payload?.impersonate_id != null;
    // const isNonPatientViewer = isImpersonating || (roleId != null && !isPatientRole(roleId));
    const patientNameParts = [result?.firstName, result?.lastName].filter(Boolean);
    const patientName = patientNameParts.join(' ').trim();
    const possessiveSuffix = patientName && /s$/i.test(patientName) ? "'" : "'s";
    const resultsTitle =
        isNonPatientViewer && patientName ? `${patientName}${possessiveSuffix} Lab Results` : 'Your Lab Results';

    // Close menu on outside click
    useEffect(() => {
        const handleClickOutside = (ev: MouseEvent) => {
            const target = ev.target as HTMLElement;
            if (target?.id === 'context-menu-overlay' || target?.id === 'context-menu-close') {
                setMenuVisible(false);
            }
            if (
                (overlayRef.current && overlayRef.current.contains(target)) ||
                (menuRef.current && menuRef.current.contains(target)) ||
                (target && target.id === 'menu-button-image')
            ) {
                return;
            }
            setMenuVisible(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleDownload = async () => {
        if (downloading) return;

        // Results with no ELIS order behind them can never produce a PDF; the
        // control is disabled for those, and this guard keeps a stray
        // programmatic call from posting a null id.
        if (!pdfAvailability.available) {
            setDownloadError(pdfAvailability.reason ?? DEFAULT_DOWNLOAD_ERROR);
            return;
        }

        setDownloading(true);

        try {
            const headers: Record<string, string> = {'Content-Type': 'application/json'};
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }
            const requestOptions: AxiosRequestConfig = {
                headers,
                responseType: 'blob',
            };

            const res = await axios.post(
                `${settings.apiURL}/api/order/pdf`,
                JSON.stringify({id: pdfAvailability.elisId}),
                requestOptions
            );

            if (res.status !== 200) {
                setDownloading(false);
                setDownloadError(DEFAULT_DOWNLOAD_ERROR);
                logError('PDF download failed with status', undefined, {status: res.status});
                return;
            }

            // Download retrieved PDF
            const filename = res.headers['content-disposition']?.split('=')[1];
            const blob = res.data;
            const newBlob = new Blob([blob], {type: 'application/pdf'});

            const msSaveOrOpenBlob = (window.navigator as MsSaveBlobNavigator).msSaveOrOpenBlob;
            if (msSaveOrOpenBlob) {
                msSaveOrOpenBlob(newBlob);
                // Reset downloading state after download completes
                setDownloading(false);
            } else {
                const objUrl = window.URL.createObjectURL(newBlob);
                const link = document.createElement('a');
                link.href = objUrl;
                link.download = filename || 'Report';
                link.click();

                setTimeout(() => {
                    window.URL.revokeObjectURL(objUrl);
                    // Reset downloading state after download completes
                    setDownloading(false);
                }, 250);
            }
        } catch (error) {
            // Download error occurred
            logError('PDF download error', error);
            setDownloading(false);
            setDownloadError(await readDownloadError(error));
        }
    };

    return (
        <DashboardLayout>
            {/* Failure and About Test Modals */}
            {(downloadError || aboutTestVisible) && (
                <div
                    ref={overlayRef}
                    id="context-menu-overlay"
                    className="fixed inset-0 bg-black/30 flex items-center justify-center z-50"
                    onClick={(ev) => {
                        if ((ev.target as HTMLElement).id === 'context-menu-overlay') {
                            setDownloadError(null);
                            setAboutTestVisible(false);
                        }
                    }}>
                    {downloadError && (
                        <div className="bg-white p-6 rounded-lg max-w-md mx-4">
                            <h3 className="text-lg font-semibold text-mld-danger mb-2">Download Failed</h3>
                            <p className="text-gray-600 mb-4">{downloadError}</p>
                            <button
                                onClick={() => setDownloadError(null)}
                                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 cursor-pointer">
                                Close
                            </button>
                        </div>
                    )}
                    {aboutTestVisible && (
                        <div className="bg-white p-6 rounded-lg max-w-md mx-4">
                            <h3 className="text-lg font-semibold text-mld-text-primary mb-2">About This Test</h3>
                            <p className="text-gray-600 mb-4">Information about {testNameString}.</p>
                            <button
                                onClick={() => setAboutTestVisible(false)}
                                className="px-4 py-2 bg-mld-primary text-white rounded hover:bg-mld-primary/90 cursor-pointer">
                                Close
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Main content area - full width */}
            <div className="relative py-3 lg:py-6">
                <div className="px-3 lg:px-6">
                    {/* Back button + optional header actions */}
                    <div className="mb-4 flex items-center justify-between gap-2">
                        <button
                            onClick={() => navigate(backTo)}
                            className="flex items-center text-mld-primary hover:text-mld-primary/80 transition-colors cursor-pointer">
                            <Icon name="chevron-left" className="h-5 w-5 mr-1" />
                            {backLabel}
                        </button>
                        {headerActions ? <div className="flex items-center gap-2">{headerActions}</div> : null}
                    </div>

                    {/* Page Title and Controls - Not in a panel */}
                    <LabResultsHeader
                        title={resultsTitle}
                        titleAdornment={
                            isNonPatientViewer ? (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setPatientContextOpen(true)}
                                    iconLeft={<Icon name="flask" className="h-3.5 w-3.5" />}>
                                    Clinical Context
                                </Button>
                            ) : null
                        }
                        onFilterChange={setFilterBy}
                        onSortChange={setSortBy}
                        onViewChange={setViewMode}
                        onDownload={pdfAvailability.available ? handleDownload : undefined}
                        downloading={downloading}
                        hasAboutTest={hasAboutTest}
                        onAboutTest={() => setAboutTestVisible(true)}
                        currentFilter={filterBy}
                        currentSort={sortBy}
                        showControls={supportsPanelView}
                    />
                </div>

                <div className="px-3 lg:px-6">
                    {/* Date Information */}
                    {(result.firstName || result.lastName) && (
                        <div className="mb-4 lg:mb-6 text-xs lg:text-sm text-gray-600 pb-4 lg:pb-6 border-b border-gray-300">
                            {`Collected on: ${createdDateString === '1/1/2001' ? 'N/A' : createdDateString} - Received on: ${createdDateString === '1/1/2001' ? 'N/A' : createdDateString} - Results generated on: ${statusDateString === '1/1/2001' ? 'N/A' : statusDateString}`}
                        </div>
                    )}
                </div>

                {/* Results Display */}
                <div className="px-3 lg:px-6 space-y-4 lg:space-y-8">
                    {/* Render based on view mode */}
                    {result.resultType === 'allergy' || result.resultType === 'blood' || isPanel ? (
                        <ResultsView
                            result={result}
                            test_type={result.resultType || (isAllergy ? 'allergy' : isBlood ? 'blood' : '')}
                            sortBy={sortBy}
                            filterBy={filterBy}
                            recommendations={result.recommendations}
                            viewMode={viewMode}
                            allPatientDataOverride={allPatientData ?? undefined}
                            patientGender={patientGender}
                            onClearFilters={() => {
                                setFilterBy('all');
                                setSortBy('newest');
                            }}
                        />
                    ) : result.resultType && result.resultType === 'binary' ? (
                        <BinaryResult result={result} hasMultipleResults={hasMultipleResults} />
                    ) : (
                        <BinaryResult result={result} hasMultipleResults={hasMultipleResults} />
                    )}
                </div>
            </div>

            {/* Combined Patient + Clinical Context modal — provider-only,
                opened from the title-row button.  Default tab is "clinical"
                because the result-review flow is the entry point. */}
            <PatientContextModal
                isOpen={patientContextOpen}
                onClose={() => setPatientContextOpen(false)}
                order={result}
                patientUserId={patientUserId}
                defaultTab="clinical"
                onSaved={onOrderRefetch}
            />
        </DashboardLayout>
    );
};

export default SingleResultContent;
